/* eslint-disable no-use-before-define */
/* eslint-disable camelcase */
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';
import helpersUtils from 'utils/Helpers';
import Empty from 'constants/empty';
import anaConsts from 'analytics/constants';
import getSiteIdFromLinkSynergy from 'services/api/creatorStoreFront/getSiteIdFromLinkSynergy';
import { getCsfRoute } from 'components/CreatorStoreFront/helpers/getCsfRoute';
import dateUtils from 'utils/Date';

const { deferTaskExecution } = helpersUtils;

// CSF-1793: Inlined feature-flag check for CSF Dynamic Commissioning. Read
// directly from `Sephora.configurationSettings` rather than importing from
// `utils/CreatorStoreFront/dynamicCommissioning` to preserve the strict
// legacy↔DC isolation boundary (no cross-imports between the two zones).
// When this returns `true` the legacy attribution flow must no-op so DC owns
// CSF attribution end-to-end.
const isCsfDynamicCommissioningEnabled = () =>
    Sephora.configurationSettings?.csf?.dynamicCommissioning?.isEnabled === true;

const U1_COLLECTION_DETAILS_PAGE_NAME = 'collectiondetail';
const U1_POST_DETAILS_PAGE_NAME = 'postdetail';
const CSF_ATTRIBUTION_STORAGE_DAYS = Storage.DAYS * 90;
const MOTOM_TRAFFIC_SOURCE_CODES = {
    FACEBOOK: 'f',
    INSTAGRAM: 'i',
    TIKTOK: 't',
    BEACONS: 'b',
    LINKTREE: 'l',
    YOUTUBE: 'y',
    SEARCH: 's',
    LANDING_PAGE: 'a',
    OTHER: 'o'
};
const VALID_MOTOM_TRAFFIC_SOURCE_CODES = new Set(Object.values(MOTOM_TRAFFIC_SOURCE_CODES));
const UNSUPPORTED_OM_MMC_SOURCES = new Set(['email', 'sms', 'paid', 'display', 'affiliate', 'aff']);
const OM_MMC_SOURCE_MAP = {
    facebook: MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK,
    fb: MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK,
    instagram: MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM,
    ig: MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM,
    tiktok: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    tt: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    bytedance: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    beacons: MOTOM_TRAFFIC_SOURCE_CODES.BEACONS,
    linktree: MOTOM_TRAFFIC_SOURCE_CODES.LINKTREE,
    youtube: MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE,
    yt: MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE,
    search: MOTOM_TRAFFIC_SOURCE_CODES.SEARCH,
    landing: MOTOM_TRAFFIC_SOURCE_CODES.LANDING_PAGE
};

/**
 * Construct U1 parameter for CSF attribution with page-based tracking
 * @param {*} param0
 * @returns
 */
export function constructU1Param({
    host_type,
    traffic_source,
    device,
    page_type,
    identifier_location = '', // comes as [identifier]_[location]
    merchant_tag,
    storefront_tag
}) {
    const prefix = [host_type, traffic_source, device, page_type, identifier_location].join('-');
    const u1Param = [prefix, merchant_tag, storefront_tag].filter(Boolean).join('_');

    // Format: [host_type]-[traffic_source]-[device]-[page_type]-[identifier]_[location]_[merchant_tag]_[storefront_tag]
    return u1Param;
}

/**
 * Map CSF page types to attribution codes based on documentation
 * @param {string} section - CSF page section
 * @param {string} identifier - Optional identifier (collection-id, post-id)
 * @returns {string} Attribution page type code
 */
export function getPageTypeForAttribution(section, identifier) {
    const CXXC = 'cxxc';
    const PXXC = 'pxxc';
    const XXXC = 'xxxc';

    // If there's an identifier, it's a detail page
    if (identifier) {
        if (section === CSF_PAGE_TYPES.POSTS) {
            return PXXC; // Post detail page
        } else if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return CXXC; // Collection detail page
        }
    }

    // For listing pages or main page
    const pageTypeMap = {
        [CSF_PAGE_TYPES.FEATURED]: XXXC,
        [CSF_PAGE_TYPES.POSTS]: XXXC, // Posts listing
        [CSF_PAGE_TYPES.COLLECTIONS]: XXXC, // Collections listing
        [CSF_PAGE_TYPES.PRODUCTS]: XXXC,
        '': XXXC
    };

    return pageTypeMap[section] || XXXC;
}

/**
 * Generate page identifier from current URL and context
 * @param {string} subAffiliateId - Creator's subAffiliateId
 * @param {string} section - Page section
 * @param {string} identifier - Optional identifier (post-id, collection-id)
 * @returns {string} Page [identifier]_[location] for U1 parameter
 */
export function generatePageIdentifierLocation(subAffiliateId, section, identifier) {
    // For detail pages with identifiers
    // returns  [collection_unique_key|post_unique_key]_[collectiondetail|postdetail]
    if (identifier) {
        if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return `${identifier}_${U1_COLLECTION_DETAILS_PAGE_NAME}`;
        } else if (section === CSF_PAGE_TYPES.POSTS) {
            return `${identifier}_${U1_POST_DETAILS_PAGE_NAME}`;
        }
    }

    // For listing pages (featured, products, collections, posts)
    // returns  [subAffiliateId]_[main|products|collections|posts]
    const location = !section || section === '' || section === CSF_PAGE_TYPES.FEATURED ? 'main' : section;

    return `${subAffiliateId}_${location}`;
}

/**
 * Check if user is entering CSF from a different creator
 * @param {string} currentSubAffiliateId - Current subAffiliateId
 * @param {object} existingData - Existing attribution data
 * @returns {boolean} True if this is a different creator
 */
function isDifferentCreator(currentSubAffiliateId, existingData) {
    if (!existingData || !existingData.u1) {
        return false;
    }

    // Extract subAffiliateId [identifier] from existing U1 parameter
    // u1Parts: [host_type]-[traffic_source]-[device]-[page_type]-[identifier]_[location]_[merchant_tag]_[storefront_tag]
    const u1Parts = existingData.u1.split('_');

    if (u1Parts.length === 0) {
        return false;
    }

    const prefixParts = u1Parts[0].split('-');

    if (prefixParts.length < 5) {
        return false;
    }

    const existingSubAffiliateId = prefixParts[4];

    if (!existingSubAffiliateId) {
        return false;
    }

    return existingSubAffiliateId !== currentSubAffiliateId;
}

/**
 * Check if user has left CSF and returned
 * @returns {boolean} True if user has left CSF
 */
function hasLeftCSF() {
    const currentPath = window.location.pathname;
    const lastCSFPath = Storage.session.getItem('lastCSFPath');

    // If we have a last CSF path and current path is not CSF, user has left
    if (lastCSFPath && !currentPath.startsWith('/creators/')) {
        return true;
    }

    // Update the last CSF path if we're currently in CSF
    if (currentPath.startsWith('/creators/')) {
        Storage.session.setItem('lastCSFPath', currentPath);

        return false;
    }

    return false;
}

/**
 * Determine traffic source based on UserAgent and referral source
 * @returns {string} Traffic source code
 */
export function getTrafficSource() {
    const referrer = document.referrer || '';
    const userAgent = navigator.userAgent || '';

    // Check URL parameters first (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const omMmc = urlParams.get('om_mmc');

    if (omMmc) {
        return normalizeTrafficSourceCode(getTrafficSourceFromOmMmc(omMmc, referrer, userAgent));
    }

    return normalizeTrafficSourceCode(getTrafficSourceFromMotomSignals({ referrer, userAgent }));
}

/**
 * Get detailed social source when om_mmc indicates social traffic
 */
function getDetailedSocialSource(omMmc, referrer, userAgent) {
    const omMmcLower = normalizeForMatch(omMmc);
    const omMmcTokens = getNormalizedTokens(omMmc);

    if (omMmcLower.includes('facebook') || tokensContain(omMmcTokens, ['fb'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK;
    }

    if (omMmcLower.includes('instagram') || tokensContain(omMmcTokens, ['ig'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM;
    }

    if (omMmcLower.includes('tiktok') || omMmcLower.includes('bytedance') || tokensContain(omMmcTokens, ['tt'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK;
    }

    if (omMmcLower.includes('youtube') || tokensContain(omMmcTokens, ['yt'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE;
    }

    return getTrafficSourceFromMotomSignals({ referrer, userAgent });
}

function getTrafficSourceFromOmMmc(omMmc, referrer, userAgent) {
    const omMmcTokens = getNormalizedTokens(omMmc);
    const source = omMmcTokens[0];

    if (!source) {
        return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
    }

    if (UNSUPPORTED_OM_MMC_SOURCES.has(source)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
    }

    if (source === 'social') {
        return getDetailedSocialSource(omMmc, referrer, userAgent);
    }

    return OM_MMC_SOURCE_MAP[source] || MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

function getTrafficSourceFromMotomSignals({ referrer = '', userAgent = '' } = {}) {
    const referrerLower = normalizeForMatch(referrer);
    const userAgentLower = normalizeForMatch(userAgent);
    const combinedSignals = `${userAgentLower} ${referrerLower}`;

    if (combinedSignals.includes('facebook')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK;
    }

    if (combinedSignals.includes('instagram') || userAgentLower.includes('igwebview') || userAgentLower.includes('igloader')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM;
    }

    if (
        userAgentLower.includes('bytedancewebview') ||
        userAgentLower.includes('tiktok') ||
        userAgentLower.includes('musically') ||
        userAgentLower.includes('bytedance') ||
        referrerLower.includes('bytedance') ||
        referrerLower.includes('tiktok')
    ) {
        return MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK;
    }

    if (referrerLower.includes('beacons.ai')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.BEACONS;
    }

    if (referrerLower.includes('linktr.ee')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.LINKTREE;
    }

    if (referrerLower.includes('youtube.com') || referrerLower.includes('youtu.be')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE;
    }

    if (isSearchEngineReferrer(referrerLower)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.SEARCH;
    }

    if (isCreatorDirectoryReferrer(referrerLower)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.LANDING_PAGE;
    }

    return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

/**
 * Check if referrer is from a search engine
 */
function isSearchEngineReferrer(referrer) {
    if (!referrer) {
        return false;
    }

    const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.', 'ask.', 'aol.'];
    const referrerLower = normalizeForMatch(referrer);

    return searchEngines.some(engine => referrerLower.includes(engine));
}

function isCreatorDirectoryReferrer(referrer) {
    return normalizeForMatch(referrer).includes('creators.sephora.com');
}

function normalizeTrafficSourceCode(source) {
    return VALID_MOTOM_TRAFFIC_SOURCE_CODES.has(source) ? source : MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

function normalizeForMatch(value) {
    return String(value || '').toLowerCase();
}

function getNormalizedTokens(value) {
    return normalizeForMatch(value)
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
}

function tokensContain(tokens, values) {
    return values.some(value => tokens.includes(value));
}

/**
 * Determine device OS for attribution
 * @returns {string} Device OS code
 */
export function getDeviceOS() {
    const userAgent = navigator.userAgent || '';

    if (userAgent.includes('iPhone')) {
        return 'i'; // iphone
    }

    if (userAgent.includes('Mac OS')) {
        return 'm'; // macos
    }

    if (userAgent.includes('Windows')) {
        return 'w'; // windows
    }

    if (userAgent.includes('Android')) {
        return 'a'; // android
    }

    // Default to 'm' for other cases
    return 'm';
}

/**
 * CSF-1793: Capture the CSF `om_mmc` value for Adobe Analytics (eVar85) into a
 * dedicated storage key, used ONLY when DC KS is on.
 *
 * Under DC KS on the legacy `captureAttributionData` is gated off, which
 * would otherwise leave Adobe `eVar85` empty because Adobe reads `om_mmc`
 * from the legacy `CSF_ATTRIBUTION_DATA` blob. This helper writes the same
 * `creatorProfile.attributionDetails.om_mmc` value Adobe gets today into a
 * dedicated `CSF_ADOBE_TRACKING_CODE` key so Adobe stays unaffected by the
 * legacy gate.
 *
 * Under DC KS off this helper is a no-op — legacy attribution still writes
 * `om_mmc` into `CSF_ATTRIBUTION_DATA` and Adobe reads from there as today.
 * The internal KS guard is defense-in-depth: callers are expected to invoke
 * this only on the KS-on branch.
 *
 * Side effects: writes ONLY the `om_mmc` value to the dedicated key. No U1
 * write, no LinkSynergy ping, no `affiliateGatewayParameters` sync, no
 * timestamp logic.
 *
 * @param {object} params
 * @param {object} params.creatorProfile - Should contain attributionDetails object
 */
export function captureCSFAdobeTrackingCode({ creatorProfile }) {
    if (!isCsfDynamicCommissioningEnabled()) {
        return;
    }

    if (!creatorProfile) {
        return;
    }

    try {
        const omMmc = creatorProfile?.attributionDetails?.om_mmc;

        if (omMmc) {
            Storage.local.setItem(LOCAL_STORAGE.CSF_ADOBE_TRACKING_CODE, omMmc, CSF_ATTRIBUTION_STORAGE_DAYS);
        } else {
            // Clear any prior Adobe key value so eVar85 does not leak a stale
            // `om_mmc` from an earlier creator into the current page view.
            // Legacy `captureAttributionData` achieves the same effect by
            // rewriting the full `CSF_ATTRIBUTION_DATA` blob with the new
            // profile's (possibly undefined) `om_mmc` field; explicit removal
            // keeps the dedicated Adobe key consistent with that behavior.
            Storage.local.removeItem(LOCAL_STORAGE.CSF_ADOBE_TRACKING_CODE);
        }
    } catch (error) {
        Sephora.logger.error('Error capturing CSF Adobe tracking code:', error);
    }
}

/**
 * Capture and store CSF attribution data with page-based tracking
 * @param {object} params - Attribution parameters
 * @param {string} params.section - CSF page section
 * @param {string} params.identifier - Optional identifier (post-id, collection-id)
 * @param {object} params.creatorProfile - Should contain attributionDetails object
 */
export function captureAttributionData({ section, identifier, creatorProfile }) {
    // CSF-1793: DC owns CSF attribution when KS is on — legacy no-op.
    if (isCsfDynamicCommissioningEnabled()) {
        return;
    }

    if (!creatorProfile) {
        return;
    }

    // Sync the affiliate gateway site ID from adapter API call to local storage
    syncAffiliateGatewaySiteIdFromApi();

    try {
        const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
        const {
            linkshareSiteId, merchantTag, mid, storefrontTag, subAffiliateId, om_mmc: omMmc
        } = attributionDetails;
        const existingData = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA);

        // Check if we should reuse existing attribution or create new one
        const shouldReuseExisting = existingData && !isDifferentCreator(subAffiliateId, existingData) && !hasLeftCSF();

        if (shouldReuseExisting) {
            // Check if attribution is still within 5-day window
            const attributionTime = new Date(existingData.timestamp);
            const now = new Date();
            const daysDiff = (now - attributionTime) / (1000 * 60 * 60 * 24);

            if (daysDiff <= 5) {
                // Reuse existing U1, just update timestamp
                const updatedData = {
                    ...existingData,
                    timestamp: new Date().toISOString()
                };

                Storage.local.setItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA, updatedData, CSF_ATTRIBUTION_STORAGE_DAYS);
                Sephora.logger.verbose('CSF attribution: Reusing existing U1 parameter');

                // Initialize LinkSynergy affiliate tracking with existing U1
                deferTaskExecution(initializeLinkSynergyAffiliate);

                return;
            }
        }

        // Create new attribution data
        const pageType = getPageTypeForAttribution(section, identifier);
        const pageIdentifierLocation = generatePageIdentifierLocation(subAffiliateId, section, identifier);

        const u1Param = constructU1Param({
            host_type: 'b',
            traffic_source: getTrafficSource(),
            device: getDeviceOS(),
            page_type: pageType,
            identifier_location: pageIdentifierLocation,
            merchant_tag: merchantTag,
            storefront_tag: storefrontTag
        });

        const attributionData = {
            u1: u1Param,
            subId: u1Param,
            linkshareSiteId,
            subAffiliateId,
            timestamp: new Date().toISOString(),
            mid,
            om_mmc: omMmc
        };

        Storage.local.setItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA, attributionData, CSF_ATTRIBUTION_STORAGE_DAYS);
        Sephora.logger.verbose('CSF attribution: Created new U1 parameter', u1Param);

        // Initialize LinkSynergy affiliate tracking with existing U1
        deferTaskExecution(initializeLinkSynergyAffiliate);
    } catch (error) {
        Sephora.logger.error('Error capturing CSF attribution data:', error);
    }
}

/**
 * Update CSF attribution data on details page with a new product ID from product tile click or ATB Cta click
 * @param {object} creatorProfile - Should contain attributionDetails object
 * @param {string} motomProductId - The motom product ID
 */
export function updateAttributionForDetailsPage(creatorProfile, motomProductId) {
    // CSF-1793: DC owns CSF attribution when KS is on — legacy no-op.
    if (isCsfDynamicCommissioningEnabled()) {
        return;
    }

    const { section, identifier } = getCsfRoute(window.location.pathname);

    if (!creatorProfile || !(section.includes(CSF_PAGE_TYPES.COLLECTIONS) || section.includes(CSF_PAGE_TYPES.POSTS)) || !identifier) {
        return;
    }

    try {
        const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
        const { merchantTag, storefrontTag, subAffiliateId } = attributionDetails;
        const existingData = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA);

        if (existingData && existingData.u1) {
            // Create new attribution data
            const pageType = getPageTypeForAttribution(section, identifier);
            const pageIdentifierLocation = generatePageIdentifierLocation(subAffiliateId, section, identifier);

            // Replace page location with motomProductId
            const pageIdentifierPart = pageIdentifierLocation.split('_')[0]; // Skip page location
            const newPageIdentifierLocation = `${pageIdentifierPart}_${motomProductId}`;

            const u1Param = constructU1Param({
                host_type: 'b',
                traffic_source: getTrafficSource(),
                device: getDeviceOS(),
                page_type: pageType,
                identifier_location: newPageIdentifierLocation,
                merchant_tag: merchantTag,
                storefront_tag: storefrontTag
            });

            const attributionData = {
                ...existingData,
                u1: u1Param,
                timestamp: new Date().toISOString()
            };

            Storage.local.setItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA, attributionData, CSF_ATTRIBUTION_STORAGE_DAYS);
            Sephora.logger.verbose(`CSF attribution: Updated U1 parameter with page identifier ${identifier} and motomProductId ${motomProductId}`, {
                oldU1: existingData.u1,
                newU1: u1Param
            });
        } else {
            Sephora.logger.verbose(`CSF attribution: No existing data to update details on page detail /${section}/${identifier}`);
        }
    } catch (error) {
        Sephora.logger.error('Error updating CSF attribution data:', error);
    }
}

/**
 * Update attribution data when navigating within CSF (preserves existing U1)
 * @param {string} handle - Creator handle (for logging)
 * @param {string} section - Page section (for logging)
 * @param {string} identifier - Optional identifier (for logging)
 */
export function updateAttributionNavigation(handle, section, identifier) {
    // CSF-1793: DC owns CSF attribution when KS is on — legacy no-op.
    if (isCsfDynamicCommissioningEnabled()) {
        return;
    }

    try {
        const existingData = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA);

        if (existingData && existingData.u1) {
            // Just update timestamp to show activity, keep existing U1
            const updatedData = {
                ...existingData,
                timestamp: new Date().toISOString()
            };

            Storage.local.setItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA, updatedData, CSF_ATTRIBUTION_STORAGE_DAYS);

            // Initialize LinkSynergy affiliate tracking with existing U1
            deferTaskExecution(initializeLinkSynergyAffiliate);

            // More detailed logging with navigation context
            Sephora.logger.verbose('CSF attribution: Updated navigation timestamp', {
                navigatedTo: `${handle}/${section}${identifier ? `/${identifier}` : ''}`,
                preservedU1: existingData.u1
            });
        } else {
            Sephora.logger.verbose('CSF attribution: No existing data to update during navigation', {
                attemptedNavigation: `${handle}/${section}${identifier ? `/${identifier}` : ''}`
            });
        }
    } catch (error) {
        Sephora.logger.error('Error updating CSF attribution navigation:', error);
    }
}

/**
 * Update CSF attribution data with a new product ID from product tile click or ATB Cta click
 * @param {string} motomProductId - The motom product ID
 */
export function updateAttributionWithProductId(motomProductId) {
    // CSF-1793: DC owns CSF attribution when KS is on — legacy no-op.
    if (isCsfDynamicCommissioningEnabled()) {
        return;
    }

    try {
        const existingData = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA);

        if (!existingData || !existingData.u1 || !motomProductId) {
            // No existing attribution data, or motomProductId, can't update
            return;
        }

        // Parse the existing u1 parameter to extract components
        const u1 = existingData.u1;
        const u1Parts = u1.split('_');

        if (!u1 || u1Parts.length < 4) {
            // No u1 data or Invalid u1 format, can't update
            return;
        }

        const prefix = u1Parts[0]; // [host_type]-[traffic_source]-[device]-[page_type]_[identifier]
        const suffix = u1Parts.slice(2).join('_'); // [merchant_tag]_[storefront_tag] (skip old page location)

        // Rebuild u1 with new motomProductId instead of page loacation
        const newU1 = `${prefix}_${motomProductId}_${suffix}`;

        // Update the stored attribution data
        const updatedData = {
            ...existingData,
            u1: newU1,
            subId: newU1, // Keep subId in sync with u1
            timestamp: new Date().toISOString() // Update timestamp for last interaction
        };

        Storage.local.setItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA, updatedData, CSF_ATTRIBUTION_STORAGE_DAYS);

        Sephora.logger.verbose('CSF attribution updated with product ID:', motomProductId);

        // Initialize LinkSynergy affiliate tracking with existing U1
        deferTaskExecution(initializeLinkSynergyAffiliate);
    } catch (error) {
        Sephora.logger.error('Error updating CSF attribution with product ID:', error);
    }
}

/**
 * Syncs affiliate gateway site ID from LinkSynergy API to local storage
 * during user interaction like add to basket etc..
 *
 * It ONLY updates siteId and other values in affiliateGatewayParameters stay the same.
 * @returns {Promise<void>}
 */
async function syncAffiliateGatewaySiteIdFromApi(siteID) {
    try {
        if (siteID) {
            const DEFAULT_COOKIE_LIFETIME = anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_COOKIE_LIFETIME;
            const currentData = Storage.local.getItem(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_PARAMETERS_STORAGE) || {};

            // Deep clone or create new object to avoid mutation issues
            const newData = JSON.parse(JSON.stringify(currentData));

            // Make sure params exists and is object
            if (!newData.params) {
                newData.params = {};
            }

            // We need to check that params.siteID is an array
            if (!Array.isArray(newData.params.siteID)) {
                newData.params.siteID = [];
            }

            // Update params.siteID[0]
            newData.params.siteID[0] = siteID;

            // Update siteId
            newData.siteId = siteID;

            // Copy the CSF attribution timestamp into affiliateGatewayParameters.linkshareTimeStamp.
            // We read the stored CSF attribution because the submitOrder API expects this timestamp.
            // If it's missing from the payload, the submitOrder API call will fail, so make sure to include it.
            const csfAttribution = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA) || {};

            if (csfAttribution.timestamp) {
                // dateUtils.formatISODateVariation returns YYYYMMDDTHHmmssZ which matches existing affiliate format
                try {
                    newData.linkshareTimeStamp = dateUtils.formatISODateVariation(csfAttribution.timestamp);
                } catch (e) {
                    // Fail silently so we don't block syncing siteID
                    Sephora.logger.verbose('Failed to format CSF attribution timestamp for linkshareTimeStamp', e);
                }
            }

            // Write back updated object
            Storage.local.setItem(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_PARAMETERS_STORAGE, newData, DEFAULT_COOKIE_LIFETIME * Storage.DAYS);

            Sephora.logger.verbose('Affiliate gateway site ID synced from API:', siteID);
        } else {
            Sephora.logger.warn('No valid siteID found in API response to sync');
        }
    } catch (error) {
        Sephora.logger.error('Error syncing affiliate gateway site ID from API:', error);
    }
}

async function initializeLinkSynergyAffiliate() {
    // CSF-1793: deferred legacy LinkSynergy work — re-check the DC KS at
    // execution time. The task may have been queued via `deferTaskExecution`
    // when the flag was off and fire after a config flip; in that case DC
    // owns CSF attribution and the legacy ping must no-op.
    if (isCsfDynamicCommissioningEnabled()) {
        return;
    }

    if (typeof window !== 'undefined') {
        try {
            const attributionData = Storage.local.getItem(LOCAL_STORAGE.CSF_ATTRIBUTION_DATA);
            const { linkshareSiteId, mid, u1 } = attributionData || Empty.Object;

            if (!attributionData || !linkshareSiteId || !mid || !u1) {
                // No existing attribution data, can't continue...
                return;
            }

            // Call the API once and use the result (siteID) to sync with local storage
            const result = await fetchSiteIdFromLinkSynergy(linkshareSiteId, mid, window.location.href, u1);
            const { siteID } = result?.data || {};

            // Sync the affiliate gateway site ID from API to local storage
            syncAffiliateGatewaySiteIdFromApi(siteID);

            sessionStorage.setItem('affiliatePingFired', 'true');

            Sephora.logger.verbose('Affiliate pixel fired!', u1);
        } catch (error) {
            Sephora.logger.error('Affiliate ping failed silently', error);
        }
    }
}

/**
 * Fetch site ID from LinkSynergy API using provided parameters.
 * @param {string} linkshareSiteId
 * @param {string} mid
 * @param {string} murl
 * @param {string} u1
 * @returns {Promise<object>} API response data
 */
export async function fetchSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1) {
    try {
        const result = await getSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1);

        Sephora.logger.verbose('LinkSynergy site ID fetched successfully:', result.data);

        return result;
    } catch (error) {
        // Handle error
        Sephora.logger.error('Error fetching site ID from LinkSynergy:', error);
        throw error;
    }
}
